import React from 'react';
import { useTranslation } from 'react-i18next';
import { displayFirm, expertFirm, expertInitials, expertPresentation, type Expert, type ExpertType } from '../utils/expertProfile';
import { contrastAgainstWhite, isHexColor, MIN_CONTRAST } from './company-theme/companyTheme';
import { useThemeLogo } from './company-theme/useThemeLogo';
import { FoldedPresentation } from './FoldedPresentation';

export type ExpertStatusTone = 'ok' | 'waiting' | 'off';

export interface ExpertCardStatus {
  key: string;
  label: string;
  tone: ExpertStatusTone;
  /** What the status is about, e.g. the project "#1 Badrum". */
  meta?: string;
}

interface ExpertCardProps {
  provider: Expert;
  type: ExpertType;
  /** 'card' for a lone expert (full width), 'row' when there are several. */
  layout: 'card' | 'row';
  /** General availability; hidden where the expert already said yes. */
  showAvailability?: boolean;
  statuses?: ExpertCardStatus[];
  action?: { label: string; icon?: string; onClick: () => void; testId?: string };
  expanded: boolean;
  onToggleProfile: () => void;
  id?: string;
  testId?: string;
  /** The expert's own theme (ClickUp 869fa6m3u): logo instead of initials, accent stripe and colours. */
  brand?: ExpertBrand | null;
}

export interface ExpertBrand {
  headingColor: string;
  accentColor: string;
  logoSrc?: string | null;
}

/**
 * The theme's colours as CSS variables (data, so inline per styling.md §6).
 * The accent colours the button only when it is readable on white.
 */
const brandStyle = (brand?: ExpertBrand | null): React.CSSProperties | undefined => {
  if (!brand || !isHexColor(brand.accentColor)) return undefined;
  return {
    '--expert-accent': brand.accentColor,
    ...(isHexColor(brand.headingColor) && contrastAgainstWhite(brand.headingColor) >= MIN_CONTRAST.headingColor ? { '--expert-heading': brand.headingColor } : {}),
    ...(contrastAgainstWhite(brand.accentColor) >= MIN_CONTRAST.accentColor
      ? { '--expert-button': brand.accentColor, '--company-link': brand.accentColor }
      : {}),
  } as React.CSSProperties;
};

/** The expert's logo when their theme has one, else their initials. */
export function ExpertAvatar({ name, type, logoSrc, large = false }: { name: string; type: ExpertType; logoSrc?: string | null; large?: boolean }) {
  return logoSrc
    ? <span className={`external-help-logo ${large ? '' : 'is-small'}`}><img src={logoSrc} alt="" /></span>
    : <span className={`external-help-avatar ${large ? 'is-large' : ''} is-${type}`} aria-hidden="true">{expertInitials(name)}</span>;
}

/** ExpertAvatar from a listed expert's saved theme (Mina experter rows). */
export function SavedExpertAvatar({ provider, type, name }: { provider?: Expert; type: ExpertType; name: string }) {
  const theme = provider?.theme && !provider.theme.isDefault ? provider.theme : null;
  const { src } = useThemeLogo(theme?.logoUrl);
  return <ExpertAvatar name={name} type={type} logoSrc={src} />;
}

/**
 * The expert card for lawyers and inspectors (ClickUp 869fk17e6), on Extern
 * hjälp and in a project's Extern hjälp tab alike: name and firm, availability,
 * area and languages, the request's state, one action and the profile.
 */
export function ExpertCard({
  provider, type, layout, showAvailability = true, statuses = [], action, expanded, onToggleProfile, id, testId, brand: brandProp
}: ExpertCardProps) {
  const { t } = useTranslation();
  // The expert's saved theme from the API (ClickUp 869fa6nt2), unless a
  // brand is given (the theme editor's live preview). Etappio's standard
  // theme shows the card as before.
  const savedTheme = !brandProp && provider.theme && !provider.theme.isDefault ? provider.theme : null;
  const { src: savedLogo } = useThemeLogo(savedTheme?.logoUrl);
  const brand = brandProp ?? (savedTheme
    ? { headingColor: savedTheme.headingColor, accentColor: savedTheme.accentColor, logoSrc: savedLogo }
    : null);
  const firm = displayFirm(provider.name, expertFirm(provider, type));
  const presentation = expertPresentation(provider, type);
  const role = t(`externalHelp.role.${type}`);
  const org = provider.organizationNumber ? t('myCompanies.orgShort', { number: provider.organizationNumber }) : '';
  // Contact only once the expert has said yes to one of the viewer's projects (the API sends it then).
  const contact = provider.contactVisible ? { phone: provider.phone, email: provider.email } : null;
  const facts = [
    ...((provider.experienceYears || 0) > 0 ? [{ icon: 'award', label: t('marketplace.provider.experience'), value: t('myCompanies.yearsValue', { years: provider.experienceYears }) }] : []),
    ...(provider.hourlyRate ? [{ icon: 'tag', label: t('marketplace.provider.hourlyRate'), value: provider.hourlyRate }] : []),
    ...(provider.responseTime ? [{ icon: 'clock', label: t('marketplace.provider.responseTime'), value: provider.responseTime }] : []),
    ...(provider.leadTime ? [{ icon: 'calendar', label: t('marketplace.provider.leadTime'), value: provider.leadTime }] : []),
    ...(provider.geographicArea ? [{ icon: 'map-pin', label: t('marketplace.provider.geographicArea'), value: provider.geographicArea }] : []),
    ...((provider.languages || []).length > 0 ? [{ icon: 'globe', label: t('marketplace.provider.languages'), value: provider.languages.join(', ') }] : []),
  ];

  // The same build-up as a company's card on "Mina byggföretag" (ClickUp
  // 869fk17e6): head with contact, then presentation, specialties, practical
  // information and the viewer's projects; folded to the head in a list.
  return (
    <article id={id} className={`external-help-expert is-${layout} ${expanded ? 'is-expanded' : ''} ${brand ? 'has-brand' : ''}`} aria-label={provider.name}
      data-testid={testId} style={brandStyle(brand)}>
      <header className="external-help-expert-head">
        <ExpertAvatar name={provider.name} type={type} logoSrc={brand?.logoSrc} large />
        <div className="external-help-expert-title">
          <h4 className="external-help-name">{provider.name}</h4>
          <p className="external-help-meta">{[role, firm, provider.geographicArea, org].filter(Boolean).join(' · ')}</p>
        </div>
        {contact && (contact.phone || contact.email) && (
          <nav className="company-card-contact external-help-contact" aria-label={t('myCompanies.contact', { name: provider.name })}>
            {contact.phone && (
              <a className="company-card-action" href={`tel:${contact.phone.replace(/\s/g, '')}`}>
                <i className="feather-phone" aria-hidden="true"></i>{contact.phone}
              </a>
            )}
            {contact.email && (
              <a className="company-card-action" href={`mailto:${contact.email}`}>
                <i className="feather-mail" aria-hidden="true"></i>{contact.email}
              </a>
            )}
          </nav>
        )}
      </header>
      <div className="external-help-expert-body">
        {showAvailability && (
          <span className={`external-help-pill ${provider.availability ? 'is-available' : 'is-off'}`}>
            {t(provider.availability ? 'externalHelp.available' : 'externalHelp.unavailable')}
          </span>
        )}
        {!expanded && (provider.languages?.length > 0) && (
          <p className="external-help-facts">
            <span><i className="feather-message-circle" aria-hidden="true"></i>{provider.languages.join(', ')}</span>
          </p>
        )}
      </div>
      {statuses.length > 0 && (
        <ul className="external-help-expert-projects" aria-label={t('externalHelp.projects', { name: provider.name })}>
          {statuses.map(status => (
            <li key={status.key} data-testid="external-help-expert-status">
              <i className="feather-folder" aria-hidden="true"></i>
              {status.meta && <span className="external-help-expert-project">{status.meta}</span>}
              <span className={`external-help-pill is-${status.tone}`}>{status.label}</span>
            </li>
          ))}
        </ul>
      )}
      {expanded && (
        <div className="external-help-expert-details">
          <section>
            <h5 className="company-card-heading">{t('externalHelp.presentation')}</h5>
            {presentation
              ? <FoldedPresentation html={presentation} />
              : <p className="external-help-presentation external-help-meta mb-0">{t('externalHelp.noPresentation')}</p>}
          </section>
          {provider.specialties?.length > 0 && (
            <section>
              <h5 className="company-card-heading">{t('externalHelp.specialties')}</h5>
              <ul className="company-card-tags">
                {provider.specialties.map(specialty => <li key={specialty}>{specialty}</li>)}
              </ul>
            </section>
          )}
          {facts.length > 0 && (
            <section>
              <h5 className="company-card-heading">{t('marketplace.provider.practicalInfo')}</h5>
              <dl className="company-card-facts">
                {facts.map(fact => (
                  <div key={fact.label}>
                    <dt><i className={`feather-${fact.icon}`} aria-hidden="true"></i>{fact.label}</dt>
                    <dd>{fact.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>
      )}
      <div className="external-help-actions">
        {action && (
          <button type="button" className="btn btn-primary external-help-invite" onClick={action.onClick} data-testid={action.testId}>
            {action.icon && <i className={`feather-${action.icon} me-1`} aria-hidden="true"></i>}
            {action.label}
          </button>
        )}
        <button type="button" className="external-help-link" aria-expanded={expanded} onClick={onToggleProfile}>
          {t(expanded ? 'externalHelp.hideProfile' : 'externalHelp.showProfile')}
        </button>
      </div>
    </article>
  );
}
